import type { BillingTableQueryParams } from '../../hooks/useBillingTableQuery';
import type {
  NoaListSortBy,
  RemittanceSource,
} from '../../services/api/types.gen';

export interface ClaimsExportParams {
  all_stages: true;
  file_format: 'xlsx' | 'csv';
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  insurance_ids?: string[];
  clinic_ids?: string[];
  hide_test_patients?: boolean;
  show_inactive_episodes?: boolean;
  hide_imported_patients?: boolean;
}

export function buildClaimsExportParams(
  listParams: BillingTableQueryParams,
  file_format: ClaimsExportParams['file_format']
): ClaimsExportParams {
  return {
    all_stages: true,
    file_format,
    search: listParams.search || undefined,
    sort_by: listParams.sort_by,
    sort_order: listParams.sort_order,
    insurance_ids: listParams.insurance_ids.length
      ? listParams.insurance_ids
      : undefined,
    clinic_ids: listParams.clinic_ids.length
      ? listParams.clinic_ids
      : undefined,
    hide_test_patients: listParams.hide_test_patients,
    show_inactive_episodes: listParams.show_inactive_episodes,
    hide_imported_patients: listParams.hide_imported_patients,
  };
}

interface RemittanceListExportParams {
  payor_id?: string[];
  source?: RemittanceSource[];
  deposit_date_from?: string;
  deposit_date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Page-wide hide-test-patients flag; always a concrete boolean so an
   *  explicit false round-trips instead of falling back to the backend
   *  default. */
  hide_test_patients: boolean;
}

export function buildRemittanceExportParams(
  params: RemittanceListExportParams,
  file_format: 'xlsx' | 'csv'
) {
  return {
    all_stages: true as const,
    file_format,
    payor_id: params.payor_id,
    source: params.source,
    deposit_date_from: params.deposit_date_from,
    deposit_date_to: params.deposit_date_to,
    search: params.search,
    sort_by: params.sort_by,
    sort_order: params.sort_order,
    hide_test_patients: params.hide_test_patients,
  };
}

interface NoaListExportParams {
  status?: string;
  patient_search?: string;
  branch_id?: string;
  payer_id?: string;
  show_inactive: boolean;
  hide_test: boolean;
  hide_imported_patients?: boolean;
  days_left?: 'all' | 'overdue' | 'due_today' | 'due_tomorrow' | 'due_2_5_days';
  errors?: 'all' | 'has_errors' | 'no_errors';
  /** Active tab's sort; forwarded so the file matches the on-screen order. */
  sort_by?: NoaListSortBy;
  sort_direction?: 'asc' | 'desc';
}

export function buildNoaExportParams(
  params: NoaListExportParams,
  file_format: 'xlsx' | 'csv'
) {
  return {
    all_stages: true as const,
    file_format,
    patient_search: params.patient_search,
    branch_id: params.branch_id,
    payer_id: params.payer_id,
    show_inactive: params.show_inactive,
    hide_test: params.hide_test,
    hide_imported_patients: params.hide_imported_patients,
    days_left: params.days_left,
    errors: params.errors,
    sort_by: params.sort_by,
    sort_direction: params.sort_direction,
  };
}
